import { useEffect, useRef, type FC } from 'react'
import toast from 'react-hot-toast'
import packageJson from '../../../package.json'
import { ToastCard } from '@/components/Toaster/ToastCard'
import useTranslate from '@/hooks/use-translate'
import {
  fetchAppMetaJson,
  isRemoteBuildNewerThanBundle,
} from '@/utils/appVersionMeta'

const POLL_INTERVAL_MS = 10 * 60 * 1000

/** Évite de réafficher le toast à chaque F5 si le bundle reste ancien (cache / dev). Réinitialisé quand le bundle rattrape le serveur. */
const SESSION_TOAST_SHOWN_FOR_BUILD_KEY = 'nabu-version-toast-shown-for'

export const NewVersionNotifier: FC = () => {
  const { t: tCommon } = useTranslate('common')
  const lastToastRemoteBuildDateRef = useRef<string | null>(null)
  const tCommonRef = useRef(tCommon)
  tCommonRef.current = tCommon

  useEffect(() => {
    const bundleBuildDate = packageJson.buildDate
    if (typeof bundleBuildDate !== 'string' || bundleBuildDate.length === 0) {
      return
    }

    const checkAndNotify = () => {
      void fetchAppMetaJson().then((meta) => {
        if (!meta) {
          return
        }
        if (!isRemoteBuildNewerThanBundle(meta.buildDate, bundleBuildDate)) {
          try {
            sessionStorage.removeItem(SESSION_TOAST_SHOWN_FOR_BUILD_KEY)
          } catch {
            /* private mode, etc. */
          }
          return
        }
        if (lastToastRemoteBuildDateRef.current === meta.buildDate) {
          return
        }
        try {
          if (
            sessionStorage.getItem(SESSION_TOAST_SHOWN_FOR_BUILD_KEY) ===
            meta.buildDate
          ) {
            return
          }
        } catch {
          /* ignore */
        }
        lastToastRemoteBuildDateRef.current = meta.buildDate

        try {
          sessionStorage.setItem(
            SESSION_TOAST_SHOWN_FOR_BUILD_KEY,
            meta.buildDate
          )
        } catch {
          /* ignore */
        }

        const tCommonSnapshot = tCommonRef.current

        toast.custom(
          (t) => (
            <ToastCard
              severity="info"
              message={tCommonSnapshot('newVersionAvailableMessage')}
              action={{
                label: tCommonSnapshot('refreshPageForNewVersion'),
                onClick: () => {
                  toast.dismiss(t.id)
                  window.location.reload()
                },
              }}
              onClose={() => toast.dismiss(t.id)}
            />
          ),
          {
            duration: Infinity,
            id: 'new-version-available',
          }
        )
      })
    }

    checkAndNotify()
    const intervalId = window.setInterval(checkAndNotify, POLL_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [])

  return null
}
